import {
  takeEvery, put, call,
} from 'redux-saga/effects';

import { requestGet, requestPost } from '../utils/request';

import {
  API_URLS,
  FETCH_OPTIONS,
  MODALS,
  SUCCESS_MODALS,
} from '../constants';
import { aDelRoleSuccess, aDisableEnableRoleSuccess, aNewRoleSuccess, aPermissionsListSuccess, aRolePermissions, aRolePermissionsSuccess } from '../reducers/admin';
import { aDisableEnableRole, aGetPermissions, aRemoveRole, aRoleInit, aSaveEditRole, aSaveNewRole } from '../reducers';
import { aRolesListSuccess, aSetIsFetching, aSetOpenModal } from '../reducers/common';

/**
 * @namespace sagas/roles
 */
/**
 * @memberof sagas/roles
 * @typedef Role
 * @type {object}
 * @property {string} description
 * @property {string} insertionDate
 * @property {string} insertionUser
 * @property {string} name
 * @property {number} roleId
 * @property {string} status
 */
/**
 * @memberof sagas/roles
 * @typedef "rolesInit/response.data"
 * @type {object}
 * @property {Array<Roles>} roles
 * @property {number} totalPages
 */
/**
 * Get list of roles
 *
 * @memberof sagas/roles
 * @async
 * @property {object} action
 * @property {object} action.payload
 * @property {boolean} action.payload.skipParams
 */
function* rolesInit() {
  let response;
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.ROLES_INIT,
      value: true,
    }));
    response = yield call(requestGet, API_URLS.ROLES, true);
    if (response.status === -1) {
      const e = new Error(response.error.message);
      e.modal = MODALS.ERROR;
      throw e;
    } else {
      yield put(aRolesListSuccess({
        roles: response.data,
      }));
    }
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.ROLES_INIT,
      value: false,
    }));
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.modal ? error.message : MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.ROLES_INIT,
      value: false,
    }));
  }
}
/**
 * @memberof sagas/roles
 * @typedef Permission
 * @type {object}
 * @property {number} permissionId
 * @property {string} name
 */
/**
 * @memberof sagas/roles
 * @typedef "getPermissions/response.data"
 * @type {object}
 * @property {Array<Permission>} permissions
 */
/**
 * Get list of all permissions
 *
 * @memberof sagas/roles
 * @async
 */
function* getPermissions() {
  let response;

  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.GET_PERMISSIONS,
      value: true,
    }));
    response = yield call(requestGet, API_URLS.PERMISSIONS, true);
    if (response.status !== -1) {
      yield put(aPermissionsListSuccess({
        permissions: response.data.permissions,
      }));
      yield put(aSetIsFetching({
        key: FETCH_OPTIONS.GET_PERMISSIONS,
        value: false,
      }));
    } else {
      const e = new Error(response.error.message);
      e.modal = MODALS.ERROR;
      throw e;
    }
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.modal ? error.message : MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.GET_PERMISSIONS,
      value: false,
    }));
  }
}

/**
 * Save new role
 *
 * @memberof sagas/roles
 * @async
 * @param {object} action
 */
function* saveNewRole(action) {
  let response;
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.ADD_ROLE,
      value: true,
    }));
    response = yield call(requestPost, API_URLS.SAVE_NEW_ROLE, action.payload, true);
    if (response.status === -1) {
      const e = new Error(response.error.message);
      e.modal = MODALS.ERROR;
      throw e;
    } else {
      yield put(aNewRoleSuccess());
      yield put(aSetOpenModal({
        modal: MODALS.SUCCESS,
        modalData: {
          message: SUCCESS_MODALS.ADD_ROLE,
          additionalMessage: 'successfully',
          modal: SUCCESS_MODALS.ADD_ROLE,
        },
        key: FETCH_OPTIONS.ADD_ROLE,
        value: false,
      }));
      yield put(aRoleInit());
    }
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.modal ? error.message : MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.ADD_ROLE,
      value: false,
    }));
  }
}

/**
 * Remove role
 *
 * @memberof sagas/roles
 * @async
 * @param {object} action
 */
function* removeRole(action) {
  let response;
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.REMOVE_ROLE,
      value: true,
    }));
    response = yield call(requestPost, API_URLS.REMOVE_ROLE, action.payload, true);
    if (response.status === -1) {
      const e = new Error(response.error.message);
      e.modal = MODALS.ERROR;
      throw e;
    } else {
      yield put(aDelRoleSuccess());
      yield put(aSetOpenModal({
        modal: MODALS.SUCCESS,
        modalData: {
          message: SUCCESS_MODALS.REMOVE_ROLE,
          additionalMessage: 'successfull',
          modal: SUCCESS_MODALS.REMOVE_ROLE,
        },
        key: FETCH_OPTIONS.REMOVE_ROLE,
        value: false,
      }));
      yield put(aRoleInit());
    }
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.modal ? error.message : MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.REMOVE_ROLE,
      value: false,
    }));
  }
}

/**
 * Change status of user
 *
 * @memberof sagas/roles
 * @param {object} action
 */
function* changeRoleStatus(action) {
  let response;
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.CHANGE_ROLE_STATUS,
      value: true,
    }));
    response = yield call(requestPost, API_URLS.DISABLE_ENABLE_ROLE, action.payload, true);
    if (response.status === -1) {
      const e = new Error(response.error.message);
      e.modal = MODALS.ERROR;
      throw e;
    } else {
      yield put(aDisableEnableRoleSuccess());
      yield put(aSetOpenModal({
        modal: MODALS.SUCCESS,
        modalData: {
          message: SUCCESS_MODALS.EDIT_STATUS,
          additionalMessage: 'successfull',
          modal: SUCCESS_MODALS.EDIT_STATUS,
        },
        key: FETCH_OPTIONS.CHANGE_ROLE_STATUS,
        value: false,
      }));
      yield put(aRoleInit());
    }
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.modal ? error.message : MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.CHANGE_ROLE_STATUS,
      value: false,
    }));
  }
}

/**
 * Get list of permissions for passed roles
 *
 * @memberof sagas/roles
 * @param {object} action
 */
function* getRolePermissions(action) {
  let response;
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.GET_ROLE_PERMISSION,
      value: true,
    }));
    response = yield call(requestGet, `${API_URLS.ROLE_PERMISSION}/${action.payload.roleId}`, true);
    if (response.status === -1) {
      const e = new Error(response.error.message);
      e.modal = MODALS.ERROR;
      throw e;
    } else {
      yield put(aRolePermissionsSuccess({
        permissions: response.data.permissions,
        forRole: action.payload.roleId,
      }));
      yield put(aSetIsFetching({
        key: FETCH_OPTIONS.GET_ROLE_PERMISSION,
        value: false,
      }));
    }
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.modal ? error.message : MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.GET_ROLE_PERMISSION,
      value: false,
    }));
  }
}

/**
 * Edit roles
 *
 * @memberof sagas/roles
 * @async
 * @param {object} action
 */
function* saveEditRole(action) {
  let response;
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.SAVE_EDIT_ROLE,
      value: true,
    }));
    response = yield call(requestPost, API_URLS.SAVE_EDIT_ROLE, action.payload, true);
    if (response.status === -1) {
      const e = new Error(response.error.message);
      e.modal = MODALS.ERROR;
      throw e;
    } else {
      yield put(aNewRoleSuccess());
      yield put(aSetOpenModal({
        modal: MODALS.SUCCESS,
        modalData: {
          message: SUCCESS_MODALS.EDIT_ROLES,
          additionalMessage: 'successfull',
          modal: SUCCESS_MODALS.EDIT_ROLES,
        },
        key: FETCH_OPTIONS.SAVE_EDIT_ROLE,
        value: false,
      }));
      yield put(aRoleInit());
    }
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.modal ? error.message : MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.SAVE_EDIT_ROLE,
      value: false,
    }));
  }
}

/**
 * @memberof sagas/roles
 * @async
 * @returns {void}
 */
export default function* rolesSaga() {
  yield takeEvery(aRoleInit, rolesInit);
  yield takeEvery(aGetPermissions, getPermissions);
  yield takeEvery(aSaveNewRole, saveNewRole);
  yield takeEvery(aRemoveRole, removeRole);
  yield takeEvery(aDisableEnableRole, changeRoleStatus);
  yield takeEvery(aRolePermissions, getRolePermissions);
  yield takeEvery(aSaveEditRole, saveEditRole);
}
